import React, { createContext, useState, useEffect, useCallback, memo } from 'react';

// Create a context for refresh-related state and functions
export const RefreshContext = createContext(null);

// Provider component that wraps your app
export const RefreshProvider = ({ children, interval = 120, onRefresh, isExternallyLoading = false }) => {
    const [timeRemaining, setTimeRemaining] = useState(interval);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Reset timer to full interval
    const resetTimer = useCallback(() => {
        setTimeRemaining(interval);
    }, [interval]);

    // Format time as MM:SS
    const getFormattedTime = useCallback(() => {
        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }, [timeRemaining]);

    // Trigger manual refresh
    const triggerRefresh = useCallback(async () => {
        if (isRefreshing || isExternallyLoading) return;

        setIsRefreshing(true);
        try {
            await onRefresh();
            resetTimer();
        } finally {
            setIsRefreshing(false);
        }
    }, [onRefresh, resetTimer, isRefreshing, isExternallyLoading]);

    // Update to avoid auto refresh when externally loading
    useEffect(() => {
        const countdownInterval = setInterval(() => {
            if (!isExternallyLoading) {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        triggerRefresh();
                        return interval;
                    }
                    return prev - 1;
                });
            }
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, [interval, triggerRefresh, isExternallyLoading]);

    // Value object to be passed to consumers
    const contextValue = {
        timeRemaining,
        formattedTime: getFormattedTime(),
        isRefreshing: isRefreshing || isExternallyLoading,
        triggerRefresh,
        resetTimer
    };

    return (
        <RefreshContext.Provider value={contextValue}>
            {children}
        </RefreshContext.Provider>
    );
};

// Memoized countdown display component that reads from context
// This prevents re-renders of parent components
export const CountdownDisplay = memo(() => {
    // This component will re-render every time the context changes
    // but it won't cause parent components to re-render
    const { formattedTime } = React.useContext(RefreshContext);

    return <span>{formattedTime}</span>;
});

// Memoized refresh button component that reads from context
export const RefreshButton = memo(({ children, className, style, variant = "primary", ...props }) => {
    const { triggerRefresh, isRefreshing } = React.useContext(RefreshContext);

    // Use React Bootstrap Button if props include variant
    if (variant) {
        const Button = require('react-bootstrap').Button;
        return (
            <Button
                onClick={triggerRefresh}
                disabled={isRefreshing || props.disabled}
                className={className}
                style={style}
                variant={variant}
                {...props}
            >
                {children || (isRefreshing ? 'Refreshing...' : 'Refresh Now')}
            </Button>
        );
    }

    // Fallback to standard button
    return (
        <button
            onClick={triggerRefresh}
            disabled={isRefreshing || props.disabled}
            className={className}
            style={style}
            {...props}
        >
            {children || (isRefreshing ? 'Refreshing...' : 'Refresh Now')}
        </button>
    );
});